import { MemoryRouter } from "react-router-dom";
import { z } from "zod";
import { SUPPORTED_LANGUAGES } from "../../../../api/src/shared/domain/language";
import {
  ANNOUNCEMENT_LANGUAGE_PARAM,
  toAnnouncementSlug,
} from "../../../../api/src/shared/domain/announcement";
import { usePinnedLanguage } from "../../hooks/use-pinned-language";
import { ANNOUNCEMENT_COMPONENTS } from "./registry";

/**
 * The language to render in. A URL parameter is untrusted input and goes
 * through Zod like every other boundary. `catch` collapses the two ways it can
 * be useless — absent, or a locale we do not have — into English, which is the
 * language every announcement is guaranteed to have.
 */
const languageSchema = z.enum(SUPPORTED_LANGUAGES).catch("en");

/**
 * One announcement, on a blank page, in the language you ask for.
 *
 * This is what the admin visualizer frames. It is deliberately NOT the reader's
 * app: no feed behind it, no session, and closing it records nothing, so an
 * operator checking the German copy cannot dismiss the announcement for
 * themselves or leave a row behind. It is still the SHIPPED component, which is
 * the whole point — a preview rebuilt in antd would be free to disagree with
 * what readers actually get.
 *
 * The language is pinned by CLONING the i18n instance rather than by calling
 * `changeLanguage`. Cloning keeps it to this page, so opening a preview in your
 * own tab does not leave the rest of the app in German, and it needs no effect
 * to set up. `useActiveLang` reads the same context, so the localized hero
 * follows the copy without a second lever.
 */
export default function AnnouncementPreviewPage({
  slug: rawSlug,
}: {
  slug: string;
}): React.JSX.Element {
  const language = languageSchema.parse(
    new URLSearchParams(globalThis.location.search).get(
      ANNOUNCEMENT_LANGUAGE_PARAM
    )
  );
  usePinnedLanguage(language);

  const slug = toAnnouncementSlug(rawSlug);
  if (slug === null) {
    return (
      <div className="flex h-dvh items-center justify-center p-6 text-center font-dm-sans text-ds-slate-600">
        No announcement is coded under "{rawSlug}".
      </div>
    );
  }

  const Announcement = ANNOUNCEMENT_COMPONENTS[slug];

  return (
    <>
      {/* A plain ground rather than the app behind it: the operator is judging
          the sheet, and a feed underneath is the one thing guaranteed to differ
          between their screen and the reader's. */}
      <div className="h-dvh w-full bg-ds-slate-200" />
      {/* A router of its own, so the announcement's buttons go NOWHERE.
          `onAct` being a no-op is not enough: each announcement pushes its own
          destination ("/gpt/chat/new", "/specialties/manage"), and those are
          private routes. Inside the admin's iframe that put the preview behind
          the auth gate, so the first click on the thing being reviewed
          destroyed the review. A `MemoryRouter` swallows the push the same way
          the Storybook decorator does, and the sheet stays up. */}
      <MemoryRouter>
        <Announcement isOpen onDismiss={() => {}} onAct={() => {}} />
      </MemoryRouter>
    </>
  );
}
